import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError } from '@utils/types/shared/base';
import {
  AdminOrganizationDetailResponse,
  UpdateAdminOrganizationPayload
} from '@utils/types/admin';
import { ADMIN_ORGANIZATIONS_QUERY_KEY } from './use-create-admin-organization';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type UpdateAdminOrganizationParams = {
  id: number | string;
  payload: UpdateAdminOrganizationPayload;
};

/**
 * Hook to update an on-prem organization
 *
 * PUT /api/admin/on-prem/organizations/{id}
 */
export const useUpdateAdminOrganization = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const updateOrganization = async ({
    id,
    payload
  }: UpdateAdminOrganizationParams) => {
    const url = `api/admin/on-prem/organizations/${id}`;
    const response = await Axios.put<AdminOrganizationDetailResponse>(
      url,
      payload
    );
    return response.data;
  };

  return useMutation<
    AdminOrganizationDetailResponse,
    AxiosError<BaseError>,
    UpdateAdminOrganizationParams
  >(updateOrganization, {
    onSuccess: (_, variables) => {
      toast.success(t('organization-updated-successfully'));
      queryClient.invalidateQueries([ADMIN_ORGANIZATIONS_QUERY_KEY]);
      queryClient.invalidateQueries(['adminOrganization', variables.id]);
      queryClient.invalidateQueries([
        'adminOrganization',
        String(variables.id)
      ]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-update-organization');
      toast.error(String(errorMessage));
    }
  });
};
